/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Scheidung-mit-Kindern_700px.jpg";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Paper } from "@mui/material";
import MuiListNativeStyled from "../../List/MuiListNativeStyled";

const GuidebookDivorceChildren = () => {
  const title = "Ratgeber - Scheidung mit Kindern | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Wie lasse ich mich ohne zu viele negative Auswirkungen auf meine Kinder scheiden und was ist rechtlich zu beachten?"
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Tipps für eine Scheidung mit Kindern"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Wie lasse ich mich ohne zu viele negative Auswirkungen auf meine Kinder scheiden und was ist rechtlich zu beachten? Wir geben Ihnen Tipps für eine
            friedliche und faire Scheidung, die allen Beteiligten die Situation erleichtert.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Offene und faire Kommunikation
          </Typography>
          <p>
            Wie Sie als Ex-Partner miteinander und mit der Scheidung umgehen, wirkt sich direkt auf Ihre Kinder aus. Daher gilt: versuchen Sie trotz der vielen
            Emotionen offen und fair mit Ihrem Partner zu kommunizieren. Kinder spüren Stimmungen und übertragen sie oft auf sich und geben sich die Schuld für
            die Trennung ihrer Eltern. Die Veränderung und insbesondere die Ungewissheit sind sehr verunsichernd für Ihr Kind.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Durch einfache Erklärungen Sicherheit geben
          </Typography>
          <p>
            Ohne Erklärung ist die neue Situation für Kinder oft nicht nachvollziehbar und kann sehr beängstigend sein. Daher ist es sehr wichtig, dass Sie
            Ihrem Kind die neue Situation möglichst früh ganz offen und einfach erklären. Vermeiden Sie dabei, Ihren Ex-Partner schlecht zu machen oder ihm
            Schuld zuzuweisen. Hierbei hilft: Erklären Sie die Situation nicht aus Ihrer Sicht, sondern aus Sicht des Kindes: Was ändert sich im Leben Ihres
            Kindes durch die Scheidung? Geben Sie Ihrem Kind Sicherheit, indem Sie die neuen Abläufe und Regelungen genau schildern und diese ggf. sogar
            aufschreiben/aufmalen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Kind bei der Planung direkt einbeziehen
          </Typography>
          <p>
            Während Sie die neue Situation besprechen, sollten Sie Ihr Kind nicht vor vollendete Tatsachen stellen, sondern nachhören, wie es sich die neue
            Situation wünschen würde. Stellen Sie bestenfalls verschiedene Varianten zur Verfügung, aus denen das Kind auswählen kann - zum Beispiel bei der
            Aufteilung der Besuchszeiten können verschiedene Varianten vorgeschlagen werden. Zudem kann es helfen, die neuen Regelungen nicht als ganz final zu
            beschreiben, sondern zu erklären, dass sie zunächst ausprobiert werden, um dann die Lösung zu finden, die für alle am besten passt.
          </p>
          <HighlightBox headline={"Tipps kurz zusammengefasst"}>
            <MuiListNativeStyled
              listItems={[
                "Die Stimmung im Auge behalten: offene und faire Kommunikation mit dem Partner",
                "Informieren: frühzeitig informieren und durch einfache Erklärungen Sicherheit geben",
                "Mit einbeziehen: das Kind mit entscheiden lassen, indem Sie z.B. Varianten für die neuen Regelungen zur Auswahl stellen",
              ]}
            />
          </HighlightBox>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was muss rechtlich beachtet werden?
          </Typography>
          <p>
            Wenn Sie sich scheiden lassen, müssen Sie das Sorgerecht und Umgangsrecht für Ihr Kind klären. Welcher der Partner soll sich hauptverantwortlich um
            das Kind kümmern? Wann besucht das Kind den Partner? Zudem muss der Kindesunterhalt geregelt werden. Sollte das Kind hauptsächlich bei einem Partner
            leben, muss der andere für den Unterhalt aufkommen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Sorgerecht
          </Typography>
          <p>
            Sind die Eltern miteinander verheiratet, üben sie das gemeinsame Sorgerecht aus. Eine Scheidung ändert nichts an diesem gemeinsamen Sorgerecht.
            Geschiedene Eltern haben also weiterhin die gemeinsame elterliche Sorge. Außer, Sie möchten eine andere Regelung finden. Behalten Sie das gemeinsame
            Sorgerecht, haben beide in gleichem Maße Mitbestimmungsrecht, was die Sorge für das Kind (Personensorge) und das Vermögen des Kindes
            (Vermögenssorge) angeht. Sprich: unter anderem bei allen Fragen zum Thema Gesundheit, Religion, Schuldbildung, Vermögen oder dem Aufenthalt des
            Kindes, haben beide Elternteile gleiches Mitspracherecht.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Umgangsrecht bei getrennt lebenden Eltern
          </Typography>
          <p>
            Der Elternteil, bei dem das Kind nicht lebt, hat das Recht sein Kind regelmäßig zu sehen. Das Umgangsrecht ist nicht davon abhängig, ob Unterhalt
            gezahlt wird oder nicht. Rechtlich sind zudem beide Elternteile (gemäß § 1684 BGB) dazu verpflichtet, alles zu unterlassen, was das Verhältnis des
            Kindes zum jeweils anderen Elternteil beeinträchtigt oder die Erziehung erschwert. Die Eltern sollten dem Kind also nichts Schlechtes über den
            Ex-Partner erzählen. Auch wenn der Frust zwischen Ihnen und Ihrem Partner eventuell groß ist, bleiben Sie Eltern und sollten daher vermeiden, Ihre
            Konflikte vor Ihrem Kind auszutragen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Umgangsregelung
          </Typography>
          <p>
            Es gibt keine feste gesetzliche Regelung für die Häufigkeit und Dauer der Besuche. Sie müssen als Eltern daher individuell eine
            Umgangsregelung/Betreuungsregelung vereinbaren, die für Sie und Ihr Kind am besten passt. Eine häufig verwendete Aufteilung ist, dass das Kind alle
            14 Tage von Freitag bis Sonntag mit Übernachtungen beim anderen Partner verbringt. Auch die Schulferien und Feiertage sind zwischen den Eltern
            aufzuteilen. Manche Eltern verständigen sich auch auf das sogenannte “Wechselmodell”. Im Wechsel lebt das Kind dann abwechselnd z. B. zwei Wochen
            beim Vater und dann zwei Wochen bei der Mutter. Dies setzt natürlich voraus, dass die Eltern sich gut miteinander absprechen können und nicht weit
            voneinander entfernt wohnen. Wenn Sie sich nicht auf eine Regelung einigen können, gibt es die Möglichkeit, beim örtlichen Jugendamt kostenlose
            Elterngespräche zu führen, um gemeinsam eine außergerichtliche Lösung zu finden.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Kindesunterhalt
          </Typography>
          <p>
            Der Elternteil, bei dem das minderjährige Kind nicht hauptsächlich lebt, muss sogenannten Kindesunterhalt zahlen. Alle Details zum Unterhalt und zur
            Berechnung des Kindesunterhalts finden Sie in unserem Spezial-Artikel zum Unterhalt.
          </p>
          <DoubleButton
            buttonText={"Mehr zum Unterhalt"}
            buttonLink={"/ratgeber/familienrecht/unterhalt"}
            backText={"Zurück zum Ratgeber"}
            backLink={"/ratgeber/familienrecht"}
          />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorceChildren;
